import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { styled } from '@mui/material';
import uuid from 'uuid';

import { SystemAvatar } from 'components/organisms/Milestones';
import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import { useTask } from 'contexts/task';
import { useUser2 } from 'contexts/user2';
import { useViewport } from 'contexts/viewport';
import { useWebsocket } from 'contexts/websocket';
import courseService from 'services/course';
import { Avatar, Box, Button, Icon, IconButton, RichTextArea, Stack, Tag, Text } from 'shell/components';
import { ACTIVITY_ACTIONS, MAX_AI_FEEDBACK_LIMIT, TYPES } from 'utils/constants';
import { buildPrompt, getChatCompletion, getHTMLWordCount, getUserPreferredFullName } from 'utils/helper';

const Mobile = ({
    bottomRef,
    role,
    task,
    isLoading,
    input,
    setInput,
    response,
    responseData,
    isPaused,
    togglePause,
    isResponseModified,
    responseAuthor,
    responseTimestamp,
    isReadOnly,
    mode,
    responseInput,
    setResponseInput,
    onClickBack,
    onClickGenerate,
    onClickEdit,
    onClickCancelEdit,
    onClickSaveEdit,
}) => {
    return (
        <RootStack>
            <HeaderStack device={'mobile'} spacing={1}>
                <Stack alignItems={'center'} direction={'row'} spacing={1}>
                    <IconButton
                        disabled={isLoading}
                        kind={'arrowLeft'}
                        size={'medium'}
                        variant={'square'}
                        onClick={onClickBack}
                    />
                    <Text variant={'sh1'} weight={'semibold'}>
                        {task.title}
                    </Text>
                </Stack>
            </HeaderStack>
            <InfoOuterStack spacing={3}>
                <Text color={(theme) => theme.palette.ensightful.shades[0]} variant={'h4'} weight={'semibold'}>
                    Get Feedback, Fast!
                </Text>
                <Stack spacing={3}>
                    <InfoInnerStack spacing={1}>
                        <Stack alignItems={'center'} direction={'row'} spacing={1}>
                            <Icon kind={'settings'} />
                            <Text weight={'semibold'}>How it Works</Text>
                        </Stack>
                        <Text>
                            The AI utilizes the assignment rubric and examples of your instructor's feedback to deliver
                            instant personalized feedback tailored this deliverable.
                        </Text>
                    </InfoInnerStack>
                    <InfoInnerStack spacing={1}>
                        <Stack alignItems={'center'} direction={'row'} spacing={1}>
                            <Icon kind={'task'} />
                            <Text weight={'semibold'}>Implementing Feedback</Text>
                        </Stack>
                        <Text>
                            The AI’s feedback is intended to supplement, not replace, human evaluation. If there’s
                            something you’re unsure of, please double check with your instructor.
                        </Text>
                    </InfoInnerStack>
                </Stack>
            </InfoOuterStack>
            <InputContainerStack>
                <RichTextArea
                    key={`your-assignment-input-${isReadOnly}`}
                    sx={{ '& > .MuiRichTextArea-root': { height: '20rem' } }}
                    error={getHTMLWordCount(input) > 2500}
                    helperText={`${getHTMLWordCount(input)}/2500 words`}
                    helperTextPosition={'end'}
                    label={'Your Assignment'}
                    labelId={'your-assignment-input'}
                    placeholder={'Please paste the assignment here'}
                    readOnly={isLoading || isReadOnly}
                    value={input}
                    onChange={setInput}
                />
            </InputContainerStack>
            <FeedbackContainerStack device={'tablet'} spacing={2}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text>AI Generated Feedback</Text>
                    {(isLoading || (response === null && responseData === null)) && (
                        <Button
                            disabled={!isLoading && response === responseData}
                            variant={'borderless'}
                            onClick={togglePause}
                        >
                            {isPaused ? 'Resume' : 'Pause'} Generation
                        </Button>
                    )}
                    {role === 'instructor' && isReadOnly && (
                        <>
                            {mode === 'feedback' ? (
                                <Button onClick={onClickEdit}>Edit Feedback</Button>
                            ) : (
                                <Stack direction={'row'} spacing={4}>
                                    <Button color={'neutral'} variant={'tertiary'} onClick={onClickCancelEdit}>
                                        Cancel
                                    </Button>
                                    <Button onClick={onClickSaveEdit}>Save Changes</Button>
                                </Stack>
                            )}
                        </>
                    )}
                </Stack>
                {mode === 'feedback' ? (
                    <>
                        {isResponseModified && (
                            <ModifiedStack alignItems={'center'} direction={'row'} spacing={2}>
                                <Avatar size={'small'} user={responseAuthor} />
                                <Stack alignItems={'flex-start'} spacing={1}>
                                    <Tag type={'role'} value={'instructor'} />
                                    <Text>
                                        {getUserPreferredFullName(responseAuthor)} modified this feedback on{' '}
                                        <Text color={'light'} component={'span'}>
                                            {moment(responseTimestamp).format('MMM D [@] hh:mma')}
                                        </Text>
                                    </Text>
                                </Stack>
                            </ModifiedStack>
                        )}
                        <FeedbackStack device={'tablet'} isResponseModified={isResponseModified}>
                            {response || isResponseModified ? (
                                <Stack direction={'row'} spacing={2}>
                                    <SystemAvatar />
                                    <Stack spacing={1}>
                                        <Text color={'light'}>Ensightful AI</Text>
                                        <Text rich>{response}</Text>
                                        <Box ref={bottomRef} />
                                    </Stack>
                                </Stack>
                            ) : (
                                <CenteredButton
                                    disabled={!input}
                                    loading={isLoading}
                                    variant={'secondary'}
                                    onClick={onClickGenerate}
                                >
                                    Generate Feedback
                                </CenteredButton>
                            )}
                        </FeedbackStack>
                    </>
                ) : (
                    <RichTextArea
                        sx={{ '& > .MuiRichTextArea-root': { height: '20rem' } }}
                        value={responseInput}
                        onChange={setResponseInput}
                    />
                )}
            </FeedbackContainerStack>
        </RootStack>
    );
};

const Tablet = ({
    bottomRef,
    role,
    task,
    isLoading,
    input,
    setInput,
    response,
    responseData,
    isPaused,
    togglePause,
    isResponseModified,
    responseAuthor,
    responseTimestamp,
    isReadOnly,
    mode,
    responseInput,
    setResponseInput,
    onClickBack,
    onClickGenerate,
    onClickEdit,
    onClickCancelEdit,
    onClickSaveEdit,
}) => {
    return (
        <RootStack>
            <HeaderStack spacing={1}>
                <Stack alignItems={'center'} direction={'row'} spacing={1}>
                    <IconButton
                        disabled={isLoading}
                        kind={'arrowLeft'}
                        size={'medium'}
                        variant={'square'}
                        onClick={onClickBack}
                    />
                    <Text variant={'sh1'} weight={'semibold'}>
                        {task.title}
                    </Text>
                </Stack>
                <Text sx={(theme) => ({ marginLeft: theme.spacing(9) })}>
                    Due: {moment(task.end_on).format('MMM D [at] hh:mma')}
                </Text>
            </HeaderStack>
            <InfoOuterStack spacing={3}>
                <Text color={(theme) => theme.palette.ensightful.shades[0]} variant={'h4'} weight={'semibold'}>
                    Get Feedback, Fast!
                </Text>
                <Stack direction={'row'} spacing={3}>
                    <InfoInnerStack spacing={1}>
                        <Stack alignItems={'center'} direction={'row'} spacing={1}>
                            <Icon kind={'settings'} />
                            <Text weight={'semibold'}>How it Works</Text>
                        </Stack>
                        <Text>
                            The AI utilizes the assignment rubric and examples of your instructor's feedback to deliver
                            instant personalized feedback tailored this deliverable.
                        </Text>
                    </InfoInnerStack>
                    <InfoInnerStack spacing={1}>
                        <Stack alignItems={'center'} direction={'row'} spacing={1}>
                            <Icon kind={'task'} />
                            <Text weight={'semibold'}>Implementing Feedback</Text>
                        </Stack>
                        <Text>
                            The AI’s feedback is intended to supplement, not replace, human evaluation. If there’s
                            something you’re unsure of, please double check with your instructor.
                        </Text>
                    </InfoInnerStack>
                </Stack>
            </InfoOuterStack>
            <InputContainerStack>
                <RichTextArea
                    key={`your-assignment-input-${isReadOnly}`}
                    sx={{ '& > .MuiRichTextArea-root': { height: '20rem' } }}
                    error={getHTMLWordCount(input) > 2500}
                    helperText={`${getHTMLWordCount(input)}/2500 words`}
                    helperTextPosition={'end'}
                    label={'Your Assignment'}
                    labelId={'your-assignment-input'}
                    placeholder={'Please paste the assignment here'}
                    readOnly={isLoading || isReadOnly}
                    value={input}
                    onChange={setInput}
                />
            </InputContainerStack>
            <FeedbackContainerStack device={'tablet'} spacing={2}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text>AI Generated Feedback</Text>
                    {(isLoading || (response === null && responseData === null)) && (
                        <Button
                            disabled={!isLoading && response === responseData}
                            variant={'borderless'}
                            onClick={togglePause}
                        >
                            {isPaused ? 'Resume' : 'Pause'} Generation
                        </Button>
                    )}
                    {role === 'instructor' && isReadOnly && (
                        <>
                            {mode === 'feedback' ? (
                                <Button onClick={onClickEdit}>Edit Feedback</Button>
                            ) : (
                                <Stack direction={'row'} spacing={4}>
                                    <Button color={'neutral'} variant={'tertiary'} onClick={onClickCancelEdit}>
                                        Cancel
                                    </Button>
                                    <Button onClick={onClickSaveEdit}>Save Changes</Button>
                                </Stack>
                            )}
                        </>
                    )}
                </Stack>
                {mode === 'feedback' ? (
                    <>
                        {isResponseModified && (
                            <ModifiedStack alignItems={'center'} direction={'row'} spacing={1}>
                                <Avatar size={'small'} user={responseAuthor} />
                                <Tag type={'role'} value={'instructor'} />
                                <Text>
                                    {getUserPreferredFullName(responseAuthor)} modified this feedback on{' '}
                                    <Text color={'light'} component={'span'}>
                                        {moment(responseTimestamp).format('MMM D [@] hh:mma')}
                                    </Text>
                                </Text>
                            </ModifiedStack>
                        )}
                        <FeedbackStack device={'tablet'} isResponseModified={isResponseModified}>
                            {response || isResponseModified ? (
                                <Stack direction={'row'} spacing={2}>
                                    <SystemAvatar />
                                    <Stack spacing={1}>
                                        <Text color={'light'}>Ensightful AI</Text>
                                        <Text rich>{response}</Text>
                                        <Box ref={bottomRef} />
                                    </Stack>
                                </Stack>
                            ) : (
                                <CenteredButton
                                    disabled={!input}
                                    loading={isLoading}
                                    variant={'secondary'}
                                    onClick={onClickGenerate}
                                >
                                    Generate Feedback
                                </CenteredButton>
                            )}
                        </FeedbackStack>
                    </>
                ) : (
                    <RichTextArea
                        sx={{ '& > .MuiRichTextArea-root': { height: '20rem' } }}
                        value={responseInput}
                        onChange={setResponseInput}
                    />
                )}
            </FeedbackContainerStack>
        </RootStack>
    );
};

const Desktop = ({
    bottomRef,
    role,
    task,
    isLoading,
    input,
    setInput,
    response,
    responseData,
    isPaused,
    togglePause,
    isResponseModified,
    responseAuthor,
    responseTimestamp,
    isReadOnly,
    mode,
    responseInput,
    setResponseInput,
    onClickBack,
    onClickGenerate,
    onClickEdit,
    onClickCancelEdit,
    onClickSaveEdit,
}) => {
    return (
        <RootStack>
            <HeaderStack spacing={1}>
                <Stack alignItems={'center'} direction={'row'} spacing={1}>
                    <IconButton
                        disabled={isLoading}
                        kind={'arrowLeft'}
                        size={'medium'}
                        variant={'square'}
                        onClick={onClickBack}
                    />
                    <Text variant={'sh1'} weight={'semibold'}>
                        {task.title}
                    </Text>
                </Stack>
                <Text sx={(theme) => ({ marginLeft: theme.spacing(9) })}>
                    Due: {moment(task.end_on).format('MMM D [at] hh:mma')}
                </Text>
            </HeaderStack>
            <HorizontalStack direction={'row'}>
                <InputContainerStack>
                    <RichTextArea
                        key={`your-assignment-input-${isReadOnly}`}
                        sx={{
                            '& > .MuiRichTextArea-root': {
                                minHeight: 'calc(100vh - 225px)',
                                maxHeight: 'calc(100vh - 225px)',
                            },
                        }}
                        error={getHTMLWordCount(input) > 2500}
                        helperText={`${getHTMLWordCount(input)}/2500 words`}
                        helperTextPosition={'end'}
                        label={'Your Assignment'}
                        labelId={'your-assignment-input'}
                        placeholder={'Please paste the assignment here'}
                        readOnly={isLoading || isReadOnly}
                        value={input}
                        onChange={setInput}
                    />
                </InputContainerStack>
                <Stack>
                    <InfoOuterStack spacing={3}>
                        <Text color={(theme) => theme.palette.ensightful.shades[0]} variant={'h4'} weight={'semibold'}>
                            Get Feedback, Fast!
                        </Text>
                        <Stack direction={'row'} spacing={3}>
                            <InfoInnerStack spacing={1}>
                                <Stack alignItems={'center'} direction={'row'} spacing={1}>
                                    <Icon kind={'settings'} />
                                    <Text weight={'semibold'}>How it Works</Text>
                                </Stack>
                                <Text>
                                    The AI utilizes the assignment rubric and examples of your instructor's feedback to
                                    deliver instant personalized feedback tailored this deliverable.
                                </Text>
                            </InfoInnerStack>
                            <InfoInnerStack spacing={1}>
                                <Stack alignItems={'center'} direction={'row'} spacing={1}>
                                    <Icon kind={'task'} />
                                    <Text weight={'semibold'}>Implementing Feedback</Text>
                                </Stack>
                                <Text>
                                    The AI’s feedback is intended to supplement, not replace, human evaluation. If
                                    there’s something you’re unsure of, please double check with your instructor.
                                </Text>
                            </InfoInnerStack>
                        </Stack>
                    </InfoOuterStack>
                    <FeedbackContainerStack device={'desktop'} spacing={2}>
                        <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                            <Text>AI Generated Feedback</Text>
                            {(isLoading || (response === null && responseData === null)) && (
                                <Button
                                    disabled={!isLoading && response === responseData}
                                    variant={'borderless'}
                                    onClick={togglePause}
                                >
                                    {isPaused ? 'Resume' : 'Pause'} Generation
                                </Button>
                            )}
                            {role === 'instructor' && isReadOnly && (
                                <>
                                    {mode === 'feedback' ? (
                                        <Button onClick={onClickEdit}>Edit Feedback</Button>
                                    ) : (
                                        <Stack direction={'row'} spacing={4}>
                                            <Button color={'neutral'} variant={'tertiary'} onClick={onClickCancelEdit}>
                                                Cancel
                                            </Button>
                                            <Button onClick={onClickSaveEdit}>Save Changes</Button>
                                        </Stack>
                                    )}
                                </>
                            )}
                        </Stack>
                        {mode === 'feedback' ? (
                            <>
                                {isResponseModified && (
                                    <ModifiedStack alignItems={'center'} direction={'row'} spacing={1}>
                                        <Avatar size={'small'} user={responseAuthor} />
                                        <Tag type={'role'} value={'instructor'} />
                                        <Text>
                                            {getUserPreferredFullName(responseAuthor)} modified this feedback on{' '}
                                            <Text color={'light'} component={'span'}>
                                                {moment(responseTimestamp).format('MMM D [@] hh:mma')}
                                            </Text>
                                        </Text>
                                    </ModifiedStack>
                                )}
                                <FeedbackStack device={'desktop'} isResponseModified={isResponseModified}>
                                    {response || isResponseModified ? (
                                        <Stack direction={'row'} spacing={2}>
                                            <SystemAvatar />
                                            <Stack spacing={1}>
                                                <Text color={'light'}>Ensightful AI</Text>
                                                <Text rich>{response}</Text>
                                                <Box ref={bottomRef} />
                                            </Stack>
                                        </Stack>
                                    ) : (
                                        <CenteredButton
                                            disabled={!input}
                                            loading={isLoading}
                                            variant={'secondary'}
                                            onClick={onClickGenerate}
                                        >
                                            Generate Feedback
                                        </CenteredButton>
                                    )}
                                </FeedbackStack>
                            </>
                        ) : (
                            <RichTextArea
                                sx={{
                                    '& > .MuiRichTextArea-root': {
                                        minHeight: 'calc(100vh - 225px)',
                                        maxHeight: 'calc(100vh - 225px)',
                                    },
                                }}
                                value={responseInput}
                                onChange={setResponseInput}
                            />
                        )}
                    </FeedbackContainerStack>
                </Stack>
            </HorizontalStack>
        </RootStack>
    );
};

const AIFeedbackOverlay = () => {
    const { auth } = useAuth();
    const { message } = useSnackbar();
    const {
        taskDrawerContent,
        aiFeedback,
        setAIFeedback,
        aiFeedbackOverlay,
        setAIFeedbackOverlay,
        setAlertDialog,
        resetAlertDialog,
    } = useTask();
    const { course, currentGroup, accountUsers, formatives } = useUser2();
    const { device } = useViewport();
    const { sendOn } = useWebsocket();

    const bottomRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [input, setInput] = useState('');
    const [response, setResponse] = useState(null);
    const [responseData, setResponseData] = useState(null);
    const [isPaused, setIsPaused] = useState(true);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [mode, setMode] = useState('feedback'); // feedback or edit
    const [responseInput, setResponseInput] = useState('');

    const numAttemptsRemaining =
        MAX_AI_FEEDBACK_LIMIT -
        (aiFeedback || []).filter(({ milestone_id }) => milestone_id === taskDrawerContent.task.milestone_id).length -
        1;

    const togglePause = () => {
        setIsPaused((current) => {
            courseService.createCourseActivity(course.id, {
                course_id: course.id,
                group_id: currentGroup.id,
                type: TYPES.AI_FEEDBACK,
                action: current ? ACTIVITY_ACTIONS.UNPAUSE : ACTIVITY_ACTIONS.PAUSE,
                data: {
                    formative: formatives.find(
                        ({ milestone_id }) => milestone_id === taskDrawerContent.task.milestone_id
                    ),
                    displayed: response,
                    available: responseData,
                },
            });
            return !current;
        });
    };

    const onClickBack = () => {
        if (mode === 'edit') {
            setAlertDialog({
                visible: true,
                title: 'Leave this page?',
                content: 'If you continue, your edits will be lost. To save your them, please click “Save Changes.”',
                actionButtonText: 'Leave',
                variant: 'destructive',
                onConfirm: () => {
                    resetAlertDialog();
                    setAIFeedbackOverlay({ open: false, aiFeedback: null });
                },
                onCancel: resetAlertDialog,
            });
        } else {
            setAIFeedbackOverlay({ open: false, aiFeedback: null });
        }
    };

    const saveFeedback = async (submission, feedback) => {
        const timestamp = new Date().getTime();
        const newAIFeedback = {
            id: uuid.v4(),
            active: true,
            created_on: timestamp,
            modified_on: timestamp,
            course_id: course.id,
            group_id: currentGroup.id,
            task_id: taskDrawerContent.task.id,
            owner_account_id: auth.owner_account_id,
            type: TYPES.MILESTONE,
            milestone_id: taskDrawerContent.task.milestone_id,
            data: [
                {
                    created_on: timestamp,
                    created_by: '',
                    submission,
                    feedback,
                },
            ],
        };
        sendOn('default', {
            payload_type: 'create_ai_feedback',
            payload: {
                ai_feedback: newAIFeedback,
            },
        });
        setAIFeedback((current) => [...current, newAIFeedback]);
        setAIFeedbackOverlay((current) => ({ ...current, aiFeedback: newAIFeedback }));
        setIsReadOnly(true);
        message.success('Feedback Saved: You can access and review it at a later time.');
    };

    const onClickGenerate = () => {
        const handleResponse = async (response) => {
            let result = '';
            const reader = response.body.getReader();
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    setIsLoading(false);
                    saveFeedback(input, result);
                    break;
                }
                const decoded = new TextDecoder('utf-8').decode(value);
                const chunks = decoded.split(',,').filter((chunk) => chunk?.length);
                const parsed = chunks.map((chunk) => JSON.parse(chunk));
                parsed.forEach((parsedChunk) => {
                    const { choices } = parsedChunk;
                    if (choices[0].finish_reason !== 'stop') {
                        const { delta } = choices[0];
                        result += delta.content;
                        setResponseData((current) => `${current || ''}${delta.content}`);
                    }
                });
            }
        };

        if (getHTMLWordCount(input) > 2500) {
            return message.error('Word count exceeded.');
        }

        setAlertDialog({
            visible: true,
            title: 'Ready to Generate Feedback?',
            content: `This will use one of your attempts. You will have ${numAttemptsRemaining} ${
                numAttemptsRemaining === 1 ? 'attempt' : 'attempts'
            } remaining.`,
            actionButtonText: 'Generate Now',
            variant: 'primary',
            onConfirm: () => {
                resetAlertDialog();
                setIsLoading(true);
                setIsPaused(false);
                const selfAccountUser = accountUsers.find(
                    ({ owner_account_id }) => owner_account_id === auth.owner_account_id
                );
                const formative = formatives.find(
                    ({ milestone_id }) => milestone_id === taskDrawerContent.task.milestone_id
                );
                const prompt = buildPrompt(getUserPreferredFullName(selfAccountUser), formative, input);
                getChatCompletion([{ role: 'user', content: prompt }])
                    .then((response) => handleResponse(response))
                    .catch((e) => {
                        setIsLoading(false);
                        message.error('Something went wrong.');
                        console.log(e);
                    });
            },
            onCancel: resetAlertDialog,
        });
    };

    const onClickEdit = () => {
        const htmlResponse = response.startsWith('<p>') ? response : `<p>${response.replaceAll('\n', '</p><p>')}</p>`;
        setResponseInput(htmlResponse);
        setMode('edit');
    };

    const onClickCancelEdit = () => {
        setAlertDialog({
            visible: true,
            title: 'Delete Edits?',
            content: 'If you continue, your edits will be lost. To save your them, please click “Save Changes.”',
            actionButtonText: 'Delete',
            variant: 'destructive',
            onConfirm: () => {
                resetAlertDialog();
                setMode('feedback');
            },
            onCancel: resetAlertDialog,
        });
    };

    const onClickSaveEdit = () => {
        const timestamp = new Date().getTime();
        const updatedAIFeedback = {
            ...aiFeedbackOverlay.aiFeedback,
            modified_on: timestamp,
            data: [
                ...aiFeedbackOverlay.aiFeedback.data,
                {
                    created_on: timestamp,
                    created_by: auth.owner_account_id,
                    submission: aiFeedbackOverlay.aiFeedback.data[0].submission,
                    feedback: responseInput,
                },
            ],
        };
        sendOn('default', {
            payload_type: 'update_ai_feedback',
            payload: {
                ai_feedback: updatedAIFeedback,
            },
        });
        setAIFeedback((current) => {
            return current.map((feedback) => {
                return feedback.id === updatedAIFeedback.id ? updatedAIFeedback : feedback;
            });
        });
        setAIFeedbackOverlay((current) => ({ ...current, aiFeedback: updatedAIFeedback }));
        setMode('feedback');
    };

    const isResponseModified = aiFeedbackOverlay.aiFeedback?.data?.length > 1;
    const responseAuthor = !isResponseModified
        ? null
        : accountUsers.find((accountUser) => {
              const [latest] = aiFeedbackOverlay.aiFeedback.data.slice(-1);
              return accountUser.owner_account_id === latest.created_by;
          });
    const responseTimestamp = !isResponseModified ? 0 : aiFeedbackOverlay.aiFeedback.data.slice(-1).created_on;

    // load existing ai feedback if it exists
    useEffect(() => {
        if (!aiFeedbackOverlay.aiFeedback) return;
        const [latest] = aiFeedbackOverlay.aiFeedback.data.slice(-1);
        setInput(latest.submission);
        setResponse(latest.feedback);
        setResponseData(latest.feedback);
        setIsReadOnly(true);
    }, [aiFeedbackOverlay.aiFeedback]);

    // pause/play response generation
    useEffect(() => {
        if (isPaused || responseData === null) return;
        setResponse((current) => {
            const dataNotDisplayed = responseData?.substring(current?.length);
            return `${current || ''}${dataNotDisplayed || ''}`;
        });
        if (bottomRef.current) bottomRef.current.scrollIntoView();
    }, [responseData, isPaused]);

    const props = {
        bottomRef,
        role: auth.role,
        task: taskDrawerContent.task,
        isLoading,
        input,
        setInput,
        response,
        responseData,
        isPaused,
        togglePause,
        isResponseModified,
        responseAuthor,
        responseTimestamp,
        isReadOnly,
        mode,
        responseInput,
        setResponseInput,
        onClickBack,
        onClickGenerate,
        onClickEdit,
        onClickCancelEdit,
        onClickSaveEdit,
    };

    if (!aiFeedbackOverlay.open) return null;

    return {
        mobile: <Mobile {...props} />,
        tablet: <Tablet {...props} />,
        desktop: <Desktop {...props} />,
    }[device];
};

const RootStack = styled(Stack)(({ theme }) => {
    return {
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: theme.zIndex.drawer + 1,
        width: '100vw',
        height: 'calc(100vh - 64px)',
        background: theme.palette.ensightful.neutral[50],
        overflowY: 'auto',
    };
});

const HeaderStack = styled(Stack)(({ device, theme }) => {
    return {
        padding: theme.spacing(3, 8),
        background: theme.palette.ensightful.shades[0],
        ...(device === 'mobile' && {
            padding: theme.spacing(5),
        }),
    };
});

const HorizontalStack = styled(Stack)(({ theme }) => {
    return {
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        '& > :nth-child(1)': {
            flex: 55,
        },
        '& > :nth-child(2)': {
            flex: 45,
        },
    };
});

const InputContainerStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(5, 4),
    };
});

const InfoOuterStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(5, 4),
        background: theme.palette.ensightful.primary[500],
    };
});

const InfoInnerStack = styled(Stack)(({ theme }) => {
    return {
        borderRadius: '12px',
        padding: theme.spacing(3),
        background: theme.palette.ensightful.shades[0],
    };
});

const FeedbackContainerStack = styled(Stack)(({ device, theme }) => {
    return {
        padding: theme.spacing(5),
        background: theme.palette.ensightful.shades[0],
        ...(device === 'tablet' && {
            borderRadius: '12px 12px 0 0',
        }),
        ...(device === 'desktop' && {
            height: 'calc(100vh - 341px)',
        }),
    };
});

const ModifiedStack = styled(Stack)(({ theme }) => {
    return {
        borderRadius: '12px 12px 0 0',
        margin: theme.spacing(0, 0, -2),
        padding: theme.spacing(4, 5, 3),
        background: theme.palette.ensightful.primary[50],
    };
});

const FeedbackStack = styled(Stack)(({ device, isResponseModified, theme }) => {
    return {
        position: 'relative',
        borderRadius: '12px',
        padding: theme.spacing(5),
        background: theme.palette.ensightful.neutral[50],
        overflowY: 'auto',
        ...(device === 'tablet' && {
            height: '530px',
        }),
        ...(device === 'desktop' && {
            height: '100%',
        }),
        ...(isResponseModified && {
            borderRadius: '0 0 12px 12px',
        }),
    };
});

const CenteredButton = styled(Button)(() => {
    return {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
    };
});

export default AIFeedbackOverlay;
